import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ExternalLink, RefreshCw, AlertCircle, CheckCircle,
  TrendingUp, PenLine, ListChecks, Loader2, Globe, FileText,
  Lightbulb, ChevronRight, Link2, Search, Sparkles,
} from "lucide-react";
import * as api from "../lib/api";
import { gradeColor } from "../lib/aeo";
import { INTELLIGENCE_DIMENSION_LABELS, INTELLIGENCE_DIMENSION_ORDER } from "../lib/articleIntelligence";

const PRIMARY = "#018D4C";

const INTENT_COLORS = {
  informational: { bg: "#EFF6FF", text: "#1D4ED8" },
  commercial:    { bg: "#FFFBEB", text: "#B45309" },
  transactional: { bg: "#ECFDF5", text: "#065F46" },
  comparison:    { bg: "#F5F3FF", text: "#6D28D9" },
  problem_aware: { bg: "#FFF1F2", text: "#BE123C" },
};

const DIAGNOSIS_COLORS = {
  good:   { bg: "#ECFDF5", text: "#059669" },
  medium: { bg: "#FFFBEB", text: "#B45309" },
  weak:   { bg: "#FEF2F2", text: "#DC2626" },
};

function IntentBadge({ intent }) {
  const c = INTENT_COLORS[intent] || { bg: "#F4F4F5", text: "#52525B" };
  return (
    <span
      className="px-2 py-0.5 text-[9px] tracking-[0.12em] font-bold uppercase rounded-full"
      style={{ background: c.bg, color: c.text }}
    >
      {intent?.replace(/_/g, " ")}
    </span>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-xl border border-zinc-100 shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-zinc-100">
        {Icon && <Icon size={14} className="text-zinc-400" />}
        <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">{title}</p>
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className="text-center">
      <p className={`text-xl font-bold ${highlight ? "text-orange-600" : "text-zinc-900"}`}>{value ?? "—"}</p>
      <p className="text-[10px] text-zinc-400 mt-0.5 uppercase tracking-widest">{label}</p>
    </div>
  );
}

function CopyField({ label, value, charLimit, onChange }) {
  const len = (value || "").length;
  const over = charLimit && len > charLimit;
  const good = charLimit && len >= charLimit * 0.8 && !over;
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5">{label}</p>
      <textarea
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        rows={label.toLowerCase().includes("meta") ? 3 : 2}
        className="w-full text-xs border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
      />
      {charLimit && (
        <p className={`text-[10px] mt-0.5 text-right ${over ? "text-orange-500" : good ? "text-green-600" : "text-zinc-400"}`}>
          {len} / {charLimit} chars
        </p>
      )}
    </div>
  );
}

export default function QueryDetailPage() {
  const { queryId } = useParams();
  const navigate = useNavigate();

  const [query, setQuery] = useState(null);
  const [loadingQuery, setLoadingQuery] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analyzeError, setAnalyzeError] = useState(null);

  // CTR fix state
  const [ctrLoading, setCtrLoading] = useState(false);
  const [ctrSuggestion, setCtrSuggestion] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editMeta, setEditMeta] = useState("");
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [ctrError, setCtrError] = useState(null);

  const [error, setError] = useState(null);
  const [intel, setIntel] = useState(null);
  const [scoringIntel, setScoringIntel] = useState(false);
  const [intelError, setIntelError] = useState(null);

  useEffect(() => {
    api.getGscQuery(queryId)
      .then(setQuery)
      .catch(() => setError("Query not found."))
      .finally(() => setLoadingQuery(false));
  }, [queryId]);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    setAnalyzeError(null);
    try {
      const res = await api.analyzeQueryPage(queryId);
      setAnalysis(res);
    } catch (e) {
      setAnalyzeError(e.response?.data?.detail || "Analysis failed.");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleScoreIntel = async () => {
    setScoringIntel(true);
    setIntelError(null);
    try {
      const res = await api.scoreArticleIntelligence(analysis.wp_post_id, {
        keyword: analysis.query,
        intent: analysis.intent,
      });
      setIntel(res);
    } catch (e) {
      setIntelError(e.response?.data?.detail || "Article Intelligence scoring failed.");
    } finally {
      setScoringIntel(false);
    }
  };

  const handleLoadCtrFix = async () => {
    setCtrLoading(true);
    setCtrError(null);
    try {
      const r = await api.suggestCtrFix(queryId);
      setCtrSuggestion(r);
      setEditTitle(r.suggested_title || "");
      setEditMeta(r.suggested_meta || "");
    } catch {
      setCtrError("Failed to generate suggestions.");
    } finally {
      setCtrLoading(false);
    }
  };

  const handleApplyCtr = async () => {
    setApplying(true);
    setCtrError(null);
    try {
      await api.applyCtrFix(queryId, { title: editTitle || undefined, meta_desc: editMeta || undefined });
      setApplied(true);
    } catch (e) {
      setCtrError(e.response?.data?.detail || "Failed to apply.");
    } finally {
      setApplying(false);
    }
  };

  const handleWrite = async (isImprove = false) => {
    try {
      const reason = isImprove
        ? `Page ranks at position ${Number(query.position).toFixed(1)} for "${query.query}" (${query.impressions} impressions). Needs content improvement to break into top 10.`
        : `Real GSC query with ${query.impressions} impressions — no page currently ranks for it. Intent: ${query.intent}.`;
      const outline = await api.generateOutline({
        title: isImprove ? (query.article_title || query.query) : query.query,
        cluster: "",
        reason,
      });
      navigate(`/dashboard/writing/${outline.id}`);
    } catch {
      setError("Failed to generate outline.");
    }
  };

  const handleFaq = async () => {
    try {
      const outline = await api.generateOutline({
        title: query.query,
        cluster: "",
        reason: `Informational GSC query with ${query.impressions} impressions — no page ranks. Generate as FAQ format to capture featured snippet.`,
      });
      navigate(`/dashboard/writing/${outline.id}`);
    } catch {
      setError("Failed to generate FAQ outline.");
    }
  };

  const isOrphan = query?.gap_type === "orphan";
  const cs = analysis?.copy_suggestions || {};
  const crawled = analysis?.crawled;
  // page_fit gates CTR/title-rewrite actions — a page that's fundamentally the wrong
  // page, or one of several competing pages, needs consolidation/a dedicated page,
  // not a title/meta edit. Prefer the freshly-analyzed value once Diagnose has run.
  const fit = analysis?.page_fit ?? query?.page_fit;
  const isCannibalization = fit === "cannibalization";
  const isWrongPage = fit === "wrong_page";

  if (loadingQuery) {
    return (
      <div className="flex items-center justify-center py-32 text-zinc-400 text-sm">
        <Loader2 size={16} className="animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (error || !query) {
    return (
      <div className="p-8 flex flex-col items-center gap-3 text-center">
        <AlertCircle size={24} className="text-red-400" />
        <p className="text-sm text-zinc-600">{error || "Query not found."}</p>
        <button onClick={() => navigate("/dashboard/query-gaps")} className="text-xs text-green-700 underline">
          Back to Query Gaps
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8 max-w-4xl mx-auto">
      {/* Back */}
      <button
        onClick={() => navigate("/dashboard/query-gaps")}
        className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-700 mb-6 transition-colors"
      >
        <ArrowLeft size={13} /> Back to Query Gaps
      </button>

      {/* Header */}
      <div className="mb-6 pb-6 border-b border-zinc-100">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <IntentBadge intent={query.intent} />
              <span className={`px-2 py-0.5 text-[9px] tracking-[0.12em] font-bold uppercase rounded-full ${
                isOrphan ? "bg-red-50 text-red-600" : "bg-orange-50 text-orange-600"
              }`}>
                {isOrphan ? "Missing Coverage" : "Underperforming"}
              </span>
            </div>
            <h1 className="text-3xl font-bold text-zinc-900 tracking-tight leading-tight">{query.query}</h1>
            {query.article_title && (
              <p className="text-xs text-zinc-500 mt-1.5">
                Page: <span className="font-medium text-zinc-700">{query.article_title}</span>
              </p>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
            {isOrphan && query.intent === "informational" && (
              <button
                onClick={handleFaq}
                className="flex items-center gap-1.5 px-3 py-2 text-[10px] font-bold border border-zinc-200 text-zinc-700 rounded-lg hover:bg-zinc-50 transition-colors"
              >
                <ListChecks size={11} strokeWidth={2.5} /> FAQ
              </button>
            )}
            {isOrphan && (
              <button
                onClick={() => handleWrite(false)}
                className="flex items-center gap-1.5 px-4 py-2 text-[10px] font-bold text-white rounded-lg hover:opacity-90 transition-opacity"
                style={{ backgroundColor: PRIMARY }}
              >
                <PenLine size={11} strokeWidth={2.5} /> Write Article
              </button>
            )}
            {!isOrphan && query.url && (
              <a
                href={query.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-2 text-[10px] font-bold border border-zinc-200 text-zinc-700 rounded-lg hover:bg-zinc-50 transition-colors"
              >
                <ExternalLink size={11} strokeWidth={2.5} /> View Page
              </a>
            )}
            {!isOrphan && isWrongPage && (
              <button
                onClick={() => handleWrite(false)}
                title="This page doesn't match the query's intent — a dedicated page is a better fix than improving this one."
                className="flex items-center gap-1.5 px-4 py-2 text-[10px] font-bold text-white rounded-lg hover:opacity-90 transition-opacity"
                style={{ backgroundColor: PRIMARY }}
              >
                <PenLine size={11} strokeWidth={2.5} /> Write Dedicated Page
              </button>
            )}
            {!isOrphan && !isWrongPage && (
              <button
                onClick={() => handleWrite(true)}
                className="flex items-center gap-1.5 px-4 py-2 text-[10px] font-bold text-white rounded-lg hover:opacity-90 transition-opacity"
                style={{ backgroundColor: PRIMARY }}
              >
                <PenLine size={11} strokeWidth={2.5} /> Improve
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-px bg-zinc-100 rounded-xl overflow-hidden mb-6 border border-zinc-100 shadow-sm">
        {[
          { label: "Impressions", value: query.impressions?.toLocaleString() },
          { label: "Clicks", value: query.clicks?.toLocaleString() },
          { label: "CTR", value: query.ctr > 0 ? `${(query.ctr * 100).toFixed(1)}%` : "—" },
          { label: "Position", value: query.position != null ? Number(query.position).toFixed(1) : "—", highlight: !isOrphan },
        ].map(s => (
          <div key={s.label} className="bg-white px-4 py-4 flex flex-col items-center">
            <Stat {...s} />
          </div>
        ))}
      </div>

      {/* Related queries — keyword overlap against other tracked queries, no analysis needed */}
      {query.related_queries?.length > 0 && (
        <div className="mb-6">
          <Section title="Related Queries" icon={Search}>
            <div className="flex flex-wrap gap-1.5">
              {query.related_queries.map(rq => (
                <button
                  key={rq.id}
                  onClick={() => navigate(`/dashboard/query-gaps/${rq.id}`)}
                  title={`${rq.impressions ?? 0} impressions${rq.position != null ? ` · pos ${Number(rq.position).toFixed(1)}` : ""}`}
                  className="px-3 py-1.5 text-xs font-medium bg-zinc-50 hover:bg-zinc-100 text-zinc-700 rounded-full transition-colors"
                >
                  {rq.query}
                </button>
              ))}
            </div>
          </Section>
        </div>
      )}

      {error && (
        <div className="mb-4 flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600">
          <AlertCircle size={13} /> {error}
        </div>
      )}

      {/* Analyze button */}
      <div className="mb-6">
        <button
          onClick={handleAnalyze}
          disabled={analyzing}
          className="flex items-center gap-2 px-5 py-2.5 text-xs font-bold text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
          style={{ backgroundColor: PRIMARY }}
        >
          {analyzing
            ? <><Loader2 size={13} className="animate-spin" /> {isOrphan || isWrongPage ? "Generating Brief…" : "Crawling & Analyzing…"}</>
            : <><Globe size={13} /> {analysis ? "Re-analyze" : isOrphan || isWrongPage ? "Generate Content Brief" : "Crawl Page & Analyze Copy"}</>
          }
        </button>
        {analyzeError && (
          <p className="text-xs text-red-600 mt-2 flex items-center gap-1"><AlertCircle size={12} /> {analyzeError}</p>
        )}
      </div>

      {/* Analysis results */}
      {analysis && (
        <div className="space-y-4 mb-6">
          {/* Crawled page data (underperforming only) */}
          {crawled && !isOrphan && (
            <Section title="Current Page" icon={Globe}>
              {crawled.error ? (
                <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12} /> Crawl failed: {crawled.error}</p>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1">Page Title</p>
                      <p className="text-zinc-700 font-medium">{crawled.title || "—"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1">H1</p>
                      <p className="text-zinc-700 font-medium">{crawled.h1 || "—"}</p>
                    </div>
                  </div>
                  {crawled.meta_description && (
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1">Meta Description</p>
                      <p className="text-xs text-zinc-600 italic">{crawled.meta_description}</p>
                    </div>
                  )}
                  {crawled.headings?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">Headings Structure</p>
                      <div className="space-y-1">
                        {crawled.headings.slice(0, 12).map((h, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="text-[9px] font-bold uppercase text-zinc-400 w-6 shrink-0">{h.tag}</span>
                            <span className={`text-xs text-zinc-700 ${h.tag === "h1" ? "font-semibold" : h.tag === "h2" ? "font-medium" : ""}`}>
                              {h.text}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-4 text-[10px] text-zinc-400 pt-1 border-t border-zinc-50">
                    <span>~{crawled.word_count?.toLocaleString()} words</span>
                    <span>HTTP {crawled.status_code}</span>
                    <a href={crawled.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-green-700 hover:underline">
                      <ExternalLink size={10} /> {crawled.url}
                    </a>
                  </div>
                </div>
              )}
            </Section>
          )}

          {/* Ranking diagnosis — why isn't this ranking higher, PRD §7/§16 */}
          {analysis.diagnosis?.length > 0 && (
            <Section title="Why Aren't We Ranking Higher?" icon={AlertCircle}>
              <div className="space-y-2">
                {analysis.diagnosis.map((d, i) => {
                  const c = DIAGNOSIS_COLORS[d.status] || DIAGNOSIS_COLORS.medium;
                  return (
                    <div key={i} className="flex items-start gap-2.5">
                      {d.status === "good"
                        ? <CheckCircle size={13} className="text-green-600 mt-0.5 shrink-0" />
                        : <AlertCircle size={13} className="mt-0.5 shrink-0" style={{ color: c.text }} />}
                      <div className="min-w-0">
                        <span className="text-xs font-semibold text-zinc-800">{d.dimension}</span>
                        <span
                          className="ml-2 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide rounded"
                          style={{ background: c.bg, color: c.text }}
                        >
                          {d.status}
                        </span>
                        <p className="text-[11px] text-zinc-500 mt-0.5">{d.note}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {/* Article Intelligence, targeted at this query's keyword/intent — article.md's
              multi-dim judgment, scored via TypeSafe against the ranking article, when one exists */}
          {analysis.wp_post_id && (
            <Section title="Article Intelligence" icon={Sparkles}>
              {!intel?.dimensions ? (
                <div className="text-center py-2">
                  <p className="text-[11px] text-zinc-400 mb-2 leading-relaxed">
                    {intelError || `Judge the ranking article's fit for this exact query — persona, intent, evidence, CTA, and ${INTELLIGENCE_DIMENSION_ORDER.length - 4} more dimensions`}
                  </p>
                  <button
                    onClick={handleScoreIntel}
                    disabled={scoringIntel}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold text-white rounded-lg disabled:opacity-50 transition-colors"
                    style={{ backgroundColor: PRIMARY }}
                  >
                    <Sparkles size={11} strokeWidth={2} />
                    {scoringIntel ? "Scoring…" : "Run Article Intelligence"}
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[10px] text-zinc-400">
                      Scored against "{analysis.query}" ({analysis.intent}) — {intel.model}
                    </p>
                    <button
                      onClick={handleScoreIntel}
                      disabled={scoringIntel}
                      className="text-[10px] font-bold text-zinc-400 hover:text-zinc-700 disabled:opacity-40 transition-colors"
                    >
                      {scoringIntel ? "…" : "Re-run"}
                    </button>
                  </div>
                  {INTELLIGENCE_DIMENSION_ORDER.map((key) => {
                    const d = intel.dimensions[key];
                    if (!d) return null;
                    const label = INTELLIGENCE_DIMENSION_LABELS[key];
                    if (d.type === "choice") {
                      return (
                        <div key={key} className="flex items-center justify-between text-[11px]">
                          <span className="text-zinc-600">{label}</span>
                          <span className="font-bold text-zinc-900 capitalize">{(d.choice || "").replace(/_/g, " ")}</span>
                        </div>
                      );
                    }
                    const c = gradeColor(d.grade);
                    return (
                      <div key={key}>
                        <div className="flex items-center justify-between text-[11px] mb-0.5">
                          <span className="text-zinc-600">{label}</span>
                          <span className="font-bold tabular-nums" style={{ color: c }}>{d.score}</span>
                        </div>
                        <div className="w-full h-1 bg-zinc-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${d.score}%`, backgroundColor: c }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Section>
          )}

          {/* AI Copy Analysis — underperforming */}
          {!isOrphan && cs.gap_analysis && (
            <Section title="AI Copy Analysis" icon={Lightbulb}>
              <div className="space-y-4">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5">Gap Analysis</p>
                  <p className="text-xs text-zinc-700 leading-relaxed">{cs.gap_analysis}</p>
                </div>
                {cs.improved_h1 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5">Improved H1</p>
                    <p className="text-xs text-zinc-900 font-semibold bg-green-50 rounded-lg px-3 py-2">{cs.improved_h1}</p>
                  </div>
                )}
                {cs.improved_intro && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5">Improved Intro</p>
                    <p className="text-xs text-zinc-700 leading-relaxed italic">{cs.improved_intro}</p>
                  </div>
                )}
                {cs.missing_sections?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">Missing Sections</p>
                    <div className="space-y-1">
                      {cs.missing_sections.map((s, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-zinc-700">
                          <ChevronRight size={11} className="text-green-500 shrink-0" />
                          {s}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {cs.copy_suggestions?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">Copy Suggestions</p>
                    <div className="space-y-2">
                      {cs.copy_suggestions.map((s, i) => (
                        <div key={i} className="bg-zinc-50 rounded-lg px-3 py-2.5">
                          <p className="text-[10px] font-bold text-zinc-500 mb-0.5">{s.section}</p>
                          <p className="text-xs text-zinc-700">{s.suggestion}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* SEO Action Plan (action.md §8/§17) — orphan (no page exists) or wrong_page (needs a dedicated page) */}
          {(isOrphan || isWrongPage) && (cs.content_brief || cs.suggested_outline) && (
            <Section title="SEO Action Plan" icon={FileText}>
              <div className="space-y-4">
                {cs.suggested_url && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5">Suggested URL</p>
                    <p className="text-xs text-zinc-900 font-mono bg-zinc-50 rounded-lg px-3 py-2 inline-block">{cs.suggested_url}</p>
                  </div>
                )}
                {cs.content_brief && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5">Brief</p>
                    <p className="text-xs text-zinc-700 leading-relaxed">{cs.content_brief}</p>
                  </div>
                )}
                {(cs.primary_topic || cs.supporting_topics?.length > 0) && (
                  <div className="grid grid-cols-2 gap-4">
                    {cs.primary_topic && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5">Primary Topic</p>
                        <p className="text-xs text-zinc-700 font-medium">{cs.primary_topic}</p>
                      </div>
                    )}
                    {cs.supporting_topics?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5">Supporting Topics</p>
                        <div className="flex flex-wrap gap-1">
                          {cs.supporting_topics.map((t, i) => (
                            <span key={i} className="px-2 py-0.5 text-[10px] bg-zinc-50 text-zinc-600 rounded-full">{t}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {cs.entities?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5">Entities to Cover</p>
                    <div className="flex flex-wrap gap-1">
                      {cs.entities.map((e, i) => (
                        <span key={i} className="px-2 py-0.5 text-[10px] bg-blue-50 text-blue-700 rounded-full">{e}</span>
                      ))}
                    </div>
                  </div>
                )}
                {cs.questions?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">Questions to Answer</p>
                    <div className="space-y-1">
                      {cs.questions.map((q, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs text-zinc-700">
                          <ChevronRight size={11} className="text-blue-500 shrink-0 mt-0.5" />
                          {q}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {cs.suggested_h1 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5">Suggested H1</p>
                    <p className="text-xs text-zinc-900 font-semibold bg-green-50 rounded-lg px-3 py-2">{cs.suggested_h1}</p>
                  </div>
                )}
                {cs.suggested_intro && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5">Opening</p>
                    <p className="text-xs text-zinc-700 leading-relaxed italic">{cs.suggested_intro}</p>
                  </div>
                )}
                {cs.suggested_outline?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">Suggested Sections</p>
                    <div className="space-y-1">
                      {cs.suggested_outline.map((s, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-zinc-700">
                          <span className="text-[10px] font-bold text-zinc-400 w-4">{i + 1}.</span>
                          {s}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {cs.cta && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5">CTA</p>
                    <p className="text-xs text-zinc-900 font-medium bg-amber-50 rounded-lg px-3 py-2">{cs.cta}</p>
                  </div>
                )}
                <div className="flex items-center gap-4 text-[10px] text-zinc-400 pt-2 border-t border-zinc-50">
                  {cs.word_count_target && <span>Target: ~{cs.word_count_target?.toLocaleString()} words</span>}
                  {cs.format_recommendation && <span>Format: {cs.format_recommendation}</span>}
                </div>
              </div>
            </Section>
          )}

          {/* Internal link suggestions — shared, grounded in real articles table */}
          {analysis.internal_links?.length > 0 && (
            <Section title="Internal Link Suggestions" icon={Link2}>
              <div className="space-y-1">
                {analysis.internal_links.map((l, i) => (
                  <a
                    key={i}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-zinc-50 text-xs group"
                  >
                    <span className="text-zinc-700 font-medium truncate">{l.title}</span>
                    <span className="flex items-center gap-1 text-zinc-400 group-hover:text-green-700 shrink-0">
                      {l.url} <ExternalLink size={10} />
                    </span>
                  </a>
                ))}
              </div>
            </Section>
          )}
        </div>
      )}

      {/* Cannibalization: editing this page's title/meta doesn't fix having multiple
          pages compete for the same query — surface it instead of offering CTR Fix. */}
      {!isOrphan && isCannibalization && (
        <Section title="Cannibalization" icon={AlertCircle}>
          <p className="text-xs text-zinc-600 leading-relaxed">
            Multiple pages rank for this query. Merge or redirect them into one canonical page —
            a title/meta edit on just this page won't resolve the overlap.
          </p>
        </Section>
      )}

      {/* CTR Fix — inline for underperforming with wp_post_id, skipped for cannibalization/wrong_page */}
      {!isOrphan && !isCannibalization && !isWrongPage && query.wp_post_id && (
        <Section title="CTR Fix" icon={TrendingUp}>
          {!ctrSuggestion && !ctrLoading && !applied && (
            <button
              onClick={handleLoadCtrFix}
              className="flex items-center gap-2 px-4 py-2 text-xs font-bold text-white rounded-lg hover:opacity-90 transition-opacity"
              style={{ backgroundColor: PRIMARY }}
            >
              <TrendingUp size={12} /> Generate Title & Meta Suggestions
            </button>
          )}
          {ctrLoading && (
            <div className="flex items-center gap-2 text-zinc-400 text-xs py-4">
              <Loader2 size={14} className="animate-spin" /> Generating suggestions…
            </div>
          )}
          {ctrError && (
            <p className="text-xs text-red-600 flex items-center gap-1"><AlertCircle size={12} /> {ctrError}</p>
          )}
          {ctrSuggestion && !applied && (
            <div className="space-y-4">
              <div>
                <p className="text-[10px] text-zinc-400 mb-2">
                  Current title: <span className="text-zinc-600">{ctrSuggestion.current_title}</span>
                </p>
                <CopyField label="Page Title" value={editTitle} charLimit={60} onChange={setEditTitle} />
              </div>
              <CopyField label="Meta Description" value={editMeta} charLimit={155} onChange={setEditMeta} />
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  onClick={handleApplyCtr}
                  disabled={applying || (!editTitle && !editMeta)}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
                  style={{ backgroundColor: PRIMARY }}
                >
                  {applying ? <Loader2 size={12} className="animate-spin" /> : <TrendingUp size={12} />}
                  {applying ? "Applying…" : "Apply to site"}
                </button>
              </div>
            </div>
          )}
          {applied && (
            <div className="flex items-center gap-2 text-green-700 text-xs py-2">
              <CheckCircle size={14} /> Changes applied.
            </div>
          )}
        </Section>
      )}
    </div>
  );
}
